/**
 * Subsite addresses
 *
 * A version can be published as a whole website at /~<slug>. Page slugs are
 * limited to [a-z0-9-] on every server write, so a first path segment that
 * starts with "~" can never be a page.
 *
 * Shared by the server, the admin and the public site, so it stays pure.
 */

import { slugify } from '../utils/slug.js';

export const SUBSITE_PREFIX = '~';
export const SUBSITE_SLUG_MAX = 32;
// Lowercase letters and digits, with single hyphens between words.
export const SUBSITE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * @param {unknown} slug - Stored without the "~"
 * @returns {boolean}
 */
export function isSubsiteSlug(slug) {
    return typeof slug === 'string'
        && slug.length <= SUBSITE_SLUG_MAX
        && SUBSITE_SLUG_PATTERN.test(slug);
}

function trimToMax(slug, max = SUBSITE_SLUG_MAX) {
    return slug.slice(0, max).replace(/-+$/, '');
}

/**
 * Default slug for a version name. Empty when the name has no Latin letters
 * or digits.
 * @param {string} name
 * @returns {string}
 */
export function subsiteSlugFrom(name) {
    return trimToMax(slugify(name));
}

/**
 * Add -2, -3 and so on until the slug is free, shortening the base so the
 * result stays within the limit.
 * @param {string} base
 * @param {Set<string>|string[]} taken
 * @returns {string} Empty when base is empty
 */
export function uniqueSubsiteSlug(base, taken) {
    const used = taken instanceof Set ? taken : new Set(taken || []);
    const start = trimToMax(String(base || ''));
    if (!start) return '';
    if (!used.has(start)) return start;
    for (let suffix = 2; ; suffix += 1) {
        const tail = `-${suffix}`;
        const candidate = `${trimToMax(start, SUBSITE_SLUG_MAX - tail.length)}${tail}`;
        if (!used.has(candidate)) return candidate;
    }
}

/**
 * @param {string} slug
 * @param {string} [rest] - Path inside the subsite, starting with "/". May
 *   carry a query string.
 * @returns {string} e.g. /~spring-2026/work, or /~spring-2026?x=1 for "/?x=1"
 */
export function subsitePath(slug, rest = '/') {
    const path = !rest ? '/' : (rest.startsWith('/') ? rest : `/${rest}`);
    const inner = path === '/' ? '' : (path.startsWith('/?') ? path.slice(1) : path);
    return `/${SUBSITE_PREFIX}${slug}${inner}`;
}

/**
 * Split a request path into the subsite slug and the path inside it.
 * @param {string} pathname - May carry a query string, which ends up in `rest`
 * @returns {null|{ slug: string|null, rest: string }}
 *   null when the path is not under "~"; slug null when the segment is not a
 *   valid subsite slug (bad encoding, uppercase, too long).
 */
export function parseSubsitePath(pathname) {
    const path = String(pathname || '');
    if (!path.startsWith('/')) return null;
    const end = path.slice(1).search(/[/?#]/);
    const segment = end < 0 ? path.slice(1) : path.slice(1, end + 1);
    const after = end < 0 ? '' : path.slice(end + 1);
    let decoded;
    try {
        decoded = decodeURIComponent(segment);
    } catch {
        return segment.startsWith(SUBSITE_PREFIX) || /^%7e/i.test(segment) ? { slug: null, rest: '/' } : null;
    }
    if (!decoded.startsWith(SUBSITE_PREFIX)) return null;
    const slug = decoded.slice(SUBSITE_PREFIX.length);
    const rest = !after ? '/' : (after.startsWith('/') ? after : `/${after}`);
    return { slug: isSubsiteSlug(slug) ? slug : null, rest };
}

export default {
    SUBSITE_PREFIX,
    SUBSITE_SLUG_MAX,
    SUBSITE_SLUG_PATTERN,
    isSubsiteSlug,
    subsiteSlugFrom,
    uniqueSubsiteSlug,
    subsitePath,
    parseSubsitePath,
};
